import classNames from 'classnames'
import { Button, BodyLong } from '@navikt/ds-react'
import PT from 'prop-types'
import React, { useState } from 'react'
import styled, { keyframes } from 'styled-components'
import DashboardConfig from 'config/DashboardConfig'
import { LabelsPropType } from 'index.pt'
import { DashboardControlPanelProps } from 'index.d'
import { SettingsFilled } from '@navikt/ds-icons'

const rotating = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`
export const DashboardControlPanelDiv = styled.div`
  padding: 1rem;
  position: relative;
  background-color: var(--navds-semantic-color-canvas-background);
  color: var(--navds-color-text-primary);
  display: flex;
  overflow: hidden;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
`
const MarginButton = styled(Button)`
  margin-right: 0.5rem;
`
const ButtonsDiv = styled.div`
  margin-right: 1rem;
  transition: all .5s ease-in-out;  
  transform: translateX(200%);
  opacity: 0;
  overflow: hidden;   
  &.open {
    opacity: 1;
    transform: translateX(0%);
    white-space: nowrap;
  }
`
const EditIcon = styled.div`
  cursor: pointer;
  &:hover {
    animation: ${rotating} 1s linear infinite;
  }
`
const DashboardControlPanel: React.FC<DashboardControlPanelProps> = ({
  addMode,
  editMode,
  labels,
  onAddChange,
  onCancelEdit,
  onEditModeOn,
  onResetEdit,
  onSaveEdit
}: DashboardControlPanelProps): JSX.Element => {
  const [buttonMessage, setButtonMessage] = useState<string | undefined>(undefined)
  const onResetEditHandler = () => {
    if (window.confirm(labels.editDashboard + '?')) {
      onResetEdit()
    }
  }

  return (
    <DashboardControlPanelDiv>
      <BodyLong>
        {buttonMessage || (
          addMode
            ? labels.dragNewWidgets
            : editMode
              ? labels.arrangeWidgets
              : ''
        )}
      </BodyLong>
      <ButtonsDiv className={classNames({ open: editMode || addMode })}>
        <>
          <MarginButton
            data-testid='c-dashboard__controlPanel-add-button-id'
            size='small'
            variant='secondary'
            onMouseEnter={() => setButtonMessage(labels.addNewWidgets)}
            onMouseLeave={() => setButtonMessage(undefined)}
            onClick={onAddChange}
          >
            {!addMode ? labels.addNewWidgets : labels.hideNewWidgets}
          </MarginButton>
          <MarginButton
            data-testid='c-dashboard__controlPanel-reset-button-id'
            size='small'
            variant='secondary'
            onMouseEnter={() => setButtonMessage(labels.resetDashboard)}
            onMouseLeave={() => setButtonMessage(undefined)}
            onClick={onResetEditHandler}
          >
            {labels.reset}
          </MarginButton>
          <MarginButton
            data-testid='c-dashboard__controlPanel-save-button-id'
            size='small'
            variant='secondary'
            onMouseEnter={() => setButtonMessage(labels.saveDashboard)}
            onMouseLeave={() => setButtonMessage(undefined)}
            onClick={onSaveEdit}
          >
            {labels.saveChanges}
          </MarginButton>
          <MarginButton
            data-testid='c-dashboard__controlPanel-cancel-button-id'
            size='small'
            variant='secondary'
            onMouseEnter={() => setButtonMessage(labels.discardChanges)}
            onMouseLeave={() => setButtonMessage(undefined)}
            onClick={onCancelEdit}
          >
            {labels.cancelChanges}
          </MarginButton>
        </>
      </ButtonsDiv>
      {!editMode && (
        <EditIcon
          title={labels.editDashboard}
          data-testid='c-dashboard__controlPanel-edit-icon-id'
          onClick={onEditModeOn}
        >
          <SettingsFilled />
        </EditIcon>
      )}
    </DashboardControlPanelDiv>
  )
}

DashboardControlPanel.propTypes = {
  addMode: PT.bool.isRequired,
  editMode: PT.bool.isRequired,
  labels: LabelsPropType.isRequired,
  onAddChange: PT.func.isRequired,
  onCancelEdit: PT.func.isRequired,
  onEditModeOn: PT.func.isRequired,
  onSaveEdit: PT.func.isRequired,
  onResetEdit: PT.func.isRequired
}

DashboardControlPanel.defaultProps = DashboardConfig as Partial<DashboardControlPanelProps>
export default DashboardControlPanel
